import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import {
  queueOfflineSale,
  generateOfflineInvoiceNumber,
  getOfflineCachedLocations,
  getOfflineCachedBankAccounts,
  getOfflineCachedSerials,
  getQueuedSerialNumberIds,
} from '@/lib/offline-db';
import {
  Banknote, CreditCard, Smartphone, Building2, Loader2, CheckCircle2, MapPin, UserCheck, UserX, AlertTriangle, History, ShoppingBag, Plus, Trash2, SplitSquareHorizontal, Receipt, Gift, FileText, WifiOff, QrCode, Award, Tag, X, Check, PartyPopper, Package,
} from 'lucide-react';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCartStore } from '@/stores/cart.store';
import { useAuthStore } from '@/stores/auth.store';
import { useLocationStore } from '@/stores/location.store';
import { usePromotionPreview } from '@/hooks/usePromotionPreview';
import { useLoyaltyRedeemPreview } from '@/hooks/useLoyaltyRedeemPreview';
import { OFFLINE_CAPABLE_MUTATION, OFFLINE_CAPABLE_QUERY } from '@/lib/offline-mutation';
import { selectOfflineSerials } from '@/lib/offline-serials';
import { applyDiscountsToTotal } from '@/lib/pos-total';
import { posService } from '@/services/pos.service';
import { formatCurrency, formatMoney2, formatRate } from '@/lib/utils';
import { aggregateTaxBreakdown, formatTaxLabel } from '@/lib/tax';
import type { PaymentMethod, Sale, Currency } from '@/types/pos';
import { paymentNeedsReference } from '@/lib/payment-reference';
import api from '@/lib/api';
import { notify } from '@/lib/toast';
import { ManagerApprovalDialog } from './ManagerApprovalDialog';
import { BankAccountField } from './BankAccountField';
import { ChequeFields, chequeFormToPayload, emptyChequeForm, isChequeFormComplete, type ChequeForm } from './ChequeFields';
import { effectiveBankAccountId, rememberBankAccountId } from '@/lib/bank-accounts';
import { evaluateSplitTender } from '@/lib/split-tender';

type SplitPayment = { id: string; method: PaymentMethod; amount: string; reference: string; bankAccountId: string; cheque: ChequeForm; giftCardCode: string; giftCardInfo: { currentBalance: number; code: string } | null };
interface CheckoutLocation {
  id: string; name: string; type: string; isDefault: boolean;
  paymentMethodTaxEnabled: boolean;
  paymentMethodTaxRules: Record<string, { rate: number; name: string; overridesProductTax: boolean }> | null;
  atlEnabled: boolean;
  defaultBuyerAtlStatus: boolean;
  furtherTaxRate: number;
  fbrSroScheduleNo: string | null;
}
interface CheckoutBankAccount { id: string; name: string; bankName: string }
interface AppliedCoupon { code: string; name: string }
interface CheckoutTenantSettings { managerPinEnabled?: boolean; managerDiscountThreshold?: number }
interface SalePromotionRow { promotion?: { name?: string } | null; discountAmount: string }
// Committed sale as returned by the API, or the locally-queued offline stand-in.
type CheckoutSaleResult = Sale & { _offline?: boolean; _cartSnapshot?: unknown; salePromotions?: SalePromotionRow[] };
const generateId = () => Math.random().toString(36).slice(2);

interface Props {
  open: boolean;
  /** POS-I59: F10 quick cash checkout - Cash, exact amount, the sale completes by itself once the total is settled. */
  quickCash?: boolean;
  onClose: () => void;
  onComplete: (sale: Sale) => void;
}

// POS-I61: stable empty fallbacks. A fresh [] on every render made the open-effect (keyed on the
// locations array) re-run every render, so going offline with the dialog open looped until React
// aborted ("Something went wrong") before the cached locations had loaded.
const NO_LOCATIONS: CheckoutLocation[] = [];
const NO_BANK_ACCOUNTS: CheckoutBankAccount[] = [];

export function CheckoutDialog({ open, quickCash = false, onClose, onComplete }: Props) {
  const { t } = useTranslation('pos');
  const paymentMethods: { method: PaymentMethod; label: string; icon: React.ReactNode }[] = [
    { method: 'CASH', label: t('paymentCash'), icon: <Banknote className="h-5 w-5" /> },
    { method: 'CARD', label: t('paymentCard'), icon: <CreditCard className="h-5 w-5" /> },
    { method: 'MOBILE_WALLET', label: t('paymentMobileWallet'), icon: <Smartphone className="h-5 w-5" /> },
    { method: 'QR_CODE', label: t('paymentQrCode'), icon: <QrCode className="h-5 w-5" /> },
    { method: 'BANK_TRANSFER', label: t('paymentBankTransfer'), icon: <Building2 className="h-5 w-5" /> },
    { method: 'CREDIT', label: t('paymentCredit'), icon: <Receipt className="h-5 w-5" /> },
    { method: 'CHEQUE', label: t('paymentCheque'), icon: <FileText className="h-5 w-5" /> },
    { method: 'GIFT_CARD', label: t('paymentGiftCard'), icon: <Gift className="h-5 w-5" /> },
  ];
  const subtotal = useCartStore((s) => s.subtotal);
  const totalTax = useCartStore((s) => s.totalTax);
  const orderDiscountAmount = useCartStore((s) => s.orderDiscountAmount);
  const grandTotal = useCartStore((s) => s.grandTotal);
  const pmTaxEligibleSubtotal = useCartStore((s) => s.pmTaxEligibleSubtotal);
  const pmTaxExemptSubtotal = useCartStore((s) => s.pmTaxExemptSubtotal);
  const items = useCartStore((s) => s.items);
  const customer = useCartStore((s) => s.customer);
  const orderDiscountType = useCartStore((s) => s.orderDiscountType);
  const orderDiscountValue = useCartStore((s) => s.orderDiscountValue);
  const notes = useCartStore((s) => s.notes);
  const clearCart = useCartStore((s) => s.clearCart);
  const queryClient = useQueryClient();

  // Promotion discount from the shared owner (same engine the committed sale uses),
  // so the Total/charge shown here equals the Total the sale posts. (T-356)

  const { user, hasPermission } = useAuthStore();
  // POS-I233: a read-only business cannot save sales (online or offline): the final action is
  // disabled with a reason instead of queueing a sale that could only fail later.
  const isViewOnly = useAuthStore((s) => s.isSubscriptionSuspended);
  const { selectedLocationId: ctxLocationId } = useLocationStore();
  const isOnline = useOnlineStatus();

  const canUseGiftCards = hasPermission('gift-cards:*');
  const canUseLoyalty = hasPermission('loyalty:*');
  const canUseFbr = hasPermission('fbr:*') || hasPermission('fbr:read');
  const canUseMultiCurrency = hasPermission('multi-currency:*');
  const canUsePromotions = hasPermission('promotions:*') || hasPermission('promotions:read');
  const canUseBank = hasPermission('bank:*') || hasPermission('bank:read');
  const canUseCheques = hasPermission('cheques:*');

  // Offline cached data — loaded from IndexedDB when offline
  const [cachedLocations, setCachedLocations] = useState<CheckoutLocation[] | null>(null);
  const [cachedBankAccounts, setCachedBankAccounts] = useState<CheckoutBankAccount[] | null>(null);
  useEffect(() => {
    if (!isOnline) {
      getOfflineCachedLocations().then((d) => setCachedLocations(d ?? []));
      getOfflineCachedBankAccounts().then((d) => setCachedBankAccounts(d ?? []));
    }
  }, [isOnline]);

  // Filter payment methods: offline = cash/card/mobile only; no gift card, cheque, or credit offline
  const OFFLINE_METHODS: PaymentMethod[] = ['CASH', 'CARD', 'MOBILE_WALLET', 'QR_CODE'];
  const availablePaymentMethods = paymentMethods.filter((pm) => {
    if (!isOnline && !OFFLINE_METHODS.includes(pm.method)) return false;
    if (pm.method === 'GIFT_CARD') return canUseGiftCards;
    if (pm.method === 'BANK_TRANSFER') return canUseBank;
    if (pm.method === 'CHEQUE') return canUseCheques;
    return true;
  });

  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod>('CASH');
  const [amountTendered, setAmountTendered] = useState('');
  // Change handed back on the sale just completed (backend figure; local fallback offline).
  const [completedChange, setCompletedChange] = useState(0);
  const [reference, setReference] = useState('');
  const [selectedBankAccountId, setSelectedBankAccountId] = useState<string>('');
  const [chequeForm, setChequeForm] = useState<ChequeForm>(() => emptyChequeForm());
  const [selectedLocationId, setSelectedLocationId] = useState<string>('');
  const [appliedPromotions, setAppliedPromotions] = useState<{ name: string; discountAmount: string }[]>([]);
  const [buyerIsOnAtl, setBuyerIsOnAtl] = useState<boolean>(false);
  const [fbrWarningDismissed, setFbrWarningDismissed] = useState(false);
  const [splitMode, setSplitMode] = useState(false);
  const [splitPayments, setSplitPayments] = useState<SplitPayment[]>([]);
  const amountRef = useRef<HTMLInputElement>(null);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<AppliedCoupon | null>(null);
  const { promotionDiscount, promotionPreview, previewFailed } = usePromotionPreview(appliedCoupon?.code ?? undefined);
  const [couponLoading, setCouponLoading] = useState(false);
  const [creditInfo, setCreditInfo] = useState<{ creditLimit: number | null; outstandingBalance: number; availableCredit: number | null } | null>(null);
  const [loyaltyInfo, setLoyaltyInfo] = useState<{ loyaltyPoints: number; cashValue: number; minRedeemPoints?: number } | null>(null);
  const [redeemPointsInput, setRedeemPointsInput] = useState('');
  const requestedRedeem = Math.min(
    Math.max(0, Math.floor(Number(redeemPointsInput) || 0)),
    loyaltyInfo?.loyaltyPoints ?? 0,
  );
  // POS-I100: below the program's minimum nothing is applied (the server refuses it too).
  const minRedeemPoints = loyaltyInfo?.minRedeemPoints ?? 0;
  const redeemBelowMinimum = requestedRedeem > 0 && requestedRedeem < minRedeemPoints;
  const redeemPoints = redeemBelowMinimum ? 0 : requestedRedeem;
  // T-370: the loyalty-redemption discount comes from the shared `/sales/preview` owner
  // (the SAME pricing the committed sale uses) — pre-tax capped + quantized to whole points —
  // replacing the old naive `redeemPoints / 100` that hardcoded rate 0.01 and ignored the
  // tenant redeemRate, the pre-tax cap, and whole-point quantization.
  const {
    loyaltyDiscount: appliedLoyaltyDiscount,
    loyaltyPointsConsumed,
    loyaltyPreviewFailed,
    loyaltyPreviewPending,
    pricing: serverPricing,
  } = useLoyaltyRedeemPreview({
    customerId: customer?.id,
    redeemPoints,
    couponCode: appliedCoupon?.code,
    // POS-I52: the server prices the sale (GST, ATL further tax, discounts, promotions, loyalty),
    // so the Total, Charge and quick-cash buttons quote exactly what Complete Sale will demand.
    buyerIsOnAtl,
    locationId: selectedLocationId,
    paymentMethod: selectedMethod,
    enabled: open,
  });
  // Gift card state
  const [giftCardCode, setGiftCardCode] = useState('');
  const [giftCardInfo, setGiftCardInfo] = useState<{ currentBalance: number; code: string } | null>(null);
  const [giftCardLoading, setGiftCardLoading] = useState(false);

  // Multi-currency state
  const [selectedCurrency, setSelectedCurrency] = useState<Currency | null>(null); // null = PKR
  const foreignBoxRef = useRef<HTMLDivElement>(null);
  // POS-I358: the foreign-currency box (rate, due, PKR Equivalent) must be fully visible when a currency is picked.
  // POS-I361: also when the dialog opens with the default currency preselected, after layout settles, so PKR Equivalent clears the bottom bar at 1366x768 / 1024.
  useEffect(() => {
    if (!selectedCurrency || !open) return;
    const id = window.requestAnimationFrame(() => foreignBoxRef.current?.scrollIntoView({ block: 'nearest' }));
    return () => window.cancelAnimationFrame(id);
  }, [selectedCurrency, open, splitMode, selectedMethod]);
  const [foreignAmount, setForeignAmount] = useState('');
  const [selectedSerials, setSelectedSerials] = useState<Record<string, string>>({});
  const [showSerialSelector, setShowSerialSelector] = useState(false);

  // Tip state. `tipPct` remembers the selected percentage so the tip can be
  // recomputed on the net base whenever that base moves (late promo resolution,
  // payment-method switch that changes pm-tax). Custom tips are absolute. (T-367)
  const [tipAmount, setTipAmount] = useState(0);
  const [tipCustomInput, setTipCustomInput] = useState('');
  const [tipMode, setTipMode] = useState<'none' | 'pct' | 'custom'>('none');
  const [tipPct, setTipPct] = useState<number | null>(null);

  // Manager approval state (T-001)
  const [managerApprovalOpen, setManagerApprovalOpen] = useState(false);

  // Fetch tenant settings to read manager PIN config
  const { data: tenantSettings } = useQuery({
    queryKey: ['tenant-settings-checkout'],
    queryFn: () => api.get('/tenant/settings').then((r) => r.data as CheckoutTenantSettings),
    staleTime: 60_000,
    enabled: isOnline,
  });
  const managerPinEnabled: boolean = tenantSettings?.managerPinEnabled ?? false;
  const managerDiscountThreshold: number = tenantSettings?.managerDiscountThreshold ?? 20; // %
  // Order discount as a share of the pre-discount subtotal — the percent the cashier typed
  // (fixed amounts convert the same way). Single owner for the approval test and its dialog.
  const orderDiscountPct =
    orderDiscountAmount > 0 && subtotal > 0 ? (orderDiscountAmount / subtotal) * 100 : 0;

  // Fetch tenant tax rule settings (fallback when location has no override)
  const { data: taxRuleSettings } = useQuery({
    queryKey: ['tenant-tax-rules'],
    queryFn: () => api.get('/tenant/tax-rule-settings').then((r) => r.data),
    staleTime: 60_000,
    enabled: isOnline,
  });

  // Fetch active currencies for the foreign-currency selector
  const { data: currencies = [] } = useQuery<Currency[]>({
    queryKey: ['currencies-active'],
    queryFn: () => api.get('/currencies/active').then((r) => r.data),
    staleTime: 10 * 60 * 1000,
    enabled: canUseMultiCurrency && isOnline,
  });

  // Fetch locations — online: from API; offline: from IndexedDB cache
  const { data: locationsFromApi } = useQuery({
    queryKey: ['locations-simple'],
    queryFn: () => api.get('/locations').then((r) => {
      const d = r.data;
      return (Array.isArray(d) ? d : d?.data ?? []) as CheckoutLocation[];
    }),
    staleTime: 60_000,
    enabled: isOnline,
  });
  const locationsData = isOnline ? locationsFromApi : (cachedLocations ?? NO_LOCATIONS);

  // Selected location object
    // Fetch available serials per product (not a bulk dump) for products with serialTracking.
    // POS-I67: offline, the serials cached from the server are offered instead (minus the ones
    // already used by sales waiting in the offline queue), so a serial-tracked phone can still be sold.
    const trackedProductIds = [...new Set(
      items
        .filter((item) => item.product.serialTracking)
        .map((item) => item.product.id)
    )];

    const { data: availableSerials = [], isError: serialFetchError } = useQuery<Array<{ id: string; productId: string; serialNo: string; status: string }>>({
      // POS-I67: must run offline too (it reads the IndexedDB cache); a default query pauses offline.
      ...OFFLINE_CAPABLE_QUERY,
      queryKey: ['available-serials', trackedProductIds, isOnline],
      queryFn: async () => {
        if (trackedProductIds.length === 0) return [];
        if (!isOnline) {
          const [cached, used] = await Promise.all([getOfflineCachedSerials(), getQueuedSerialNumberIds()]);
          return selectOfflineSerials(cached, used, trackedProductIds);
        }
        // Fetch per-product to avoid pulling all tenant serials
        const results = await Promise.all(
          trackedProductIds.map((productId) =>
            api.get('/serial-numbers/available', { params: { productId, limit: 500 } })
              .then((res) => Array.isArray(res.data) ? res.data : res.data?.data ?? []),
          ),
        );
        return results.flat();
      },
      enabled: trackedProductIds.length > 0 && open,
      staleTime: isOnline ? 5 * 60 * 1000 : 0,
      retry: 1,
    });

    // Selected location object
  const selectedLocation = locationsData?.find((l) => l.id === selectedLocationId) ?? null;

  // Effective payment method tax rules: location (if enabled) > tenant
  const effectivePmTaxRules = (
    selectedLocation?.paymentMethodTaxEnabled && selectedLocation?.paymentMethodTaxRules
      ? selectedLocation.paymentMethodTaxRules
      : (taxRuleSettings?.paymentMethodTaxRules ?? null)
  ) as Record<string, { rate: number; name: string }> | null;

  const pmTaxRules = effectivePmTaxRules;
  const hasPaymentMethodTax = !!pmTaxRules && Object.keys(pmTaxRules).length > 0;

  // When payment method tax overrides are active, the grand total changes per method.
  // Only ICT-eligible items (skipPaymentMethodTax=false) get the method rate.
  // Packaged goods (skipPaymentMethodTax=true) keep their own embedded GST and are added as-is.
  //
  // Order discount is applied proportionally to the pm-tax-eligible base only.
  const eligibleFraction = subtotal > 0 ? pmTaxEligibleSubtotal / subtotal : 1;
  const eligibleDiscount = orderDiscountAmount * eligibleFraction;
  const netEligibleBase = Math.max(0, pmTaxEligibleSubtotal - eligibleDiscount); // pre-tax base for ICT items

  const currentMethodRate = hasPaymentMethodTax ? (pmTaxRules?.[selectedMethod]?.rate ?? null) : null;
  const cashRate = hasPaymentMethodTax ? (pmTaxRules?.['CASH']?.rate ?? null) : null;

  // Effective totals when payment method overrides product/category taxes
  const effectiveTax = currentMethodRate !== null
    ? (netEligibleBase * currentMethodRate) / 100
    : totalTax;
  // Base total before promotion (pm-tax override path recomputes tax on the eligible base).
  const totalBeforePromotion = hasPaymentMethodTax && currentMethodRate !== null
    ? netEligibleBase + effectiveTax + pmTaxExemptSubtotal
    : grandTotal;
  // Subtract the auto-applied promotion so the displayed/charged total matches the
  // committed sale (settleSaleTotals subtracts promotionDiscountAmount). (T-356)
  // SSOT: this is the single "net payable before tip" figure. Tip %, the custom
  // tip input, and the tender auto-fill all derive from it so tender == displayed
  // Total and change == 0 for promo+tip. Tip is charged on the NET (post-promotion)
  // amount — gratuity is never charged on a discount the customer never pays. (T-356)
  // Subtract BOTH the auto-applied promotion AND the (capped/quantized) loyalty redemption so
  // the displayed/charged Total matches the committed sale (settleSaleTotals subtracts promotion
  // then loyalty). On a failed loyalty preview we do NOT subtract loyalty — the server still caps
  // on commit; we only avoid understating the Total while the check is unavailable. (T-356 / T-370)
  // POS-I52: when the server has priced this exact cart (buyer ATL status, location and method
  // included) its total is THE figure: it already nets promotions and loyalty and adds the GST
  // and further tax the sale will post. The local sum above is only the fallback while the
  // preview is loading, failed or offline.
  const netPayableBeforeTip = serverPricing
    ? Math.round(serverPricing.totalAmount * 100) / 100
    : applyDiscountsToTotal(
        totalBeforePromotion,
        promotionDiscount,
        loyaltyPreviewFailed ? 0 : appliedLoyaltyDiscount,
      );
  const effectiveGrandTotal = netPayableBeforeTip + tipAmount;

  // POS-I359: the Default currency is the till currency. For a plain cash sale it is preselected once per opening and the
  // amount due in it is prefilled; the cashier can switch back to PKR. Everything stored and taxed stays PKR.
  const autoForeignRef = useRef(false);
  const defaultAppliedRef = useRef(false);
  const defaultCurrency = currencies.find((c) => c.isDefault && c.isActive && c.code !== 'PKR' && Number(c.exchangeRate) > 0) ?? null;
  const foreignDueText = (c: Currency, pkr: number) => (Math.round((pkr / (Number(c.exchangeRate) || 1)) * 100) / 100).toFixed(2);
  useEffect(() => {
    if (!autoForeignRef.current || !selectedCurrency) return;
    if (splitMode || selectedMethod !== 'CASH') {
      // another way of paying: back to rupees
      autoForeignRef.current = false;
      setSelectedCurrency(null);
      setForeignAmount('');
      setAmountTendered(String(effectiveGrandTotal));
      return;
    }
    const due = foreignDueText(selectedCurrency, effectiveGrandTotal);
    setForeignAmount(due);
    setAmountTendered((Math.round(parseFloat(due) * Number(selectedCurrency.exchangeRate) * 100) / 100).toFixed(2));
  }, [selectedCurrency, splitMode, selectedMethod, effectiveGrandTotal]);

  // Tax savings vs cash (visible incentive to pay digitally)
  const taxSavings =
    hasPaymentMethodTax && selectedMethod !== 'CASH' && cashRate !== null && currentMethodRate !== null
      ? Math.max(0, ((cashRate - currentMethodRate) / 100) * netEligibleBase)
      : 0;

  // Effective ATL: location atlEnabled overrides tenant atlEnabled
  const effectiveAtlEnabled = selectedLocation?.atlEnabled || (taxRuleSettings?.atlEnabled ?? false);

  // Bank accounts — online: cashier-safe options endpoint (id/name only, T-479); offline: IndexedDB cache
  const { data: bankAccountsFromApi = [] } = useQuery({
    queryKey: ['bank-accounts-options'],
    queryFn: () => api.get('/bank-accounts/options').then((r) => r.data as CheckoutBankAccount[]),
    staleTime: 60_000,
    enabled: isOnline,
  });
  const bankAccounts: CheckoutBankAccount[] = isOnline
    ? bankAccountsFromApi
    : (cachedBankAccounts ?? NO_BANK_ACCOUNTS);

  // FBR pre-flight: fetch FBR environment to show missing HS code warnings
  const { data: fbrSettings } = useQuery({
    queryKey: ['fbr-settings-simple'],
    queryFn: () => api.get('/fbr/settings').then((r) => r.data as { fbrEnvironment: 'OFF' | 'STAGING' | 'LIVE' }),
    staleTime: 60_000,
    enabled: canUseFbr && isOnline,
  });

  const fbrActive = fbrSettings && fbrSettings.fbrEnvironment !== 'OFF';
  const missingHsCodeItems = fbrActive
    ? items.filter((item) => !item.product.hsCode || item.product.hsCode === '0000.0000')
    : [];

  // Customer purchase history — last 3 sales when a customer is selected (online only)
  const { data: customerHistory } = useQuery({
    queryKey: ['customer-history-pos', customer?.id],
    queryFn: () => api.get('/sales', { params: { customerId: customer!.id, status: 'COMPLETED', limit: 3, page: 1 } }).then((r) => {
      const d = r.data;
      return (Array.isArray(d) ? d : d?.data ?? []) as { id: string; invoiceNumber: string; totalAmount: string | number; grandTotal?: string | number; createdAt: string; status: string }[];
    }),
    enabled: !!customer?.id && isOnline,
    staleTime: 30_000,
  });

  const splitEval = evaluateSplitTender(splitPayments, effectiveGrandTotal);
  const change = splitMode ? splitEval.change : Math.max(0, Number(amountTendered || 0) - effectiveGrandTotal);
  const taxBreakdown = aggregateTaxBreakdown(items.map((item) => item.taxBreakdown));

  // When location changes, re-evaluate ATL default (location > tenant) — only when ATL is enabled
  useEffect(() => {
    if (!open) return;
    if (customer?.isOnAtl !== undefined) {
      setBuyerIsOnAtl(customer.isOnAtl);
      return;
    }
    if (selectedLocation?.atlEnabled) {
      setBuyerIsOnAtl(selectedLocation.defaultBuyerAtlStatus);
    } else if (taxRuleSettings?.atlEnabled) {
      setBuyerIsOnAtl(taxRuleSettings.defaultBuyerAtlStatus ?? false);
    } else {
      setBuyerIsOnAtl(false);
    }
  }, [selectedLocationId, selectedLocation, open, customer, taxRuleSettings]);

  useEffect(() => {
    if (open) {
      // Default to the promotion-aware total so Change is computed against the
      // real (charged) total, not the pre-discount amount. (T-356)
      setAmountTendered(String(effectiveGrandTotal));
      setSelectedMethod('CASH');
      setReference('');
      setSelectedBankAccountId('');
      setChequeForm(emptyChequeForm(customer?.name ?? ''));
      setAppliedPromotions([]);
      setFbrWarningDismissed(false);
      setSplitMode(false);
      setSplitPayments([]);
      setAppliedCoupon(null);
      setCouponCode('');
      setCreditInfo(null);
      setLoyaltyInfo(null);
      setRedeemPointsInput('');
      setGiftCardCode('');
      setGiftCardInfo(null);
      saleMutation.reset();
      // Reset tip
      setTipAmount(0);
      setTipCustomInput('');
      setTipMode('none');
      setTipPct(null);
      // Reset currency (POS-I359: the default-currency effect below applies it again)
      defaultAppliedRef.current = false;
      autoForeignRef.current = false;
      setSelectedCurrency(null);
      setForeignAmount('');
      // Pre-select: user's assigned location > location context > default location > first location
      const userLoc = user?.locationId ? locationsData?.find((l) => l.id === user.locationId) : null;
      const ctxLoc = ctxLocationId ? locationsData?.find((l) => l.id === ctxLocationId) : null;
      const defaultLoc = locationsData?.find((l) => l.isDefault);
      setSelectedLocationId(userLoc?.id ?? ctxLoc?.id ?? defaultLoc?.id ?? locationsData?.[0]?.id ?? '');
      setTimeout(() => amountRef.current?.select(), 100);
    }
    // Intentionally keyed on dialog-open + base inputs only: this resets the form on
    // open. Including saleMutation/user/ctxLocationId would reset mid-edit or every
    // render. (T-407 exhaustive-deps: deliberate partial deps.)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, grandTotal, locationsData]);

  useEffect(() => {
    if (!open) { defaultAppliedRef.current = false; autoForeignRef.current = false; return; }
    if (defaultAppliedRef.current || !defaultCurrency || !canUseMultiCurrency || !isOnline) return;
    if (splitMode || selectedMethod !== 'CASH' || effectiveGrandTotal <= 0) return;
    defaultAppliedRef.current = true;
    autoForeignRef.current = true;
    setSelectedCurrency(defaultCurrency);
  }, [open, grandTotal, locationsData, defaultCurrency, canUseMultiCurrency, isOnline, splitMode, selectedMethod, effectiveGrandTotal]);

  // Unified tender/tip resync (T-367). Whenever the NET payable base moves — the
  // promotion preview resolving after a cold-cache open (T-356), a payment-method
  // switch that changes pm-tax, or a location override — recompute the tip on the
  // net post-promotion base (the `netPayableBeforeTip` SSOT) and refill the tender
  // so the displayed net+tip and the tender always agree (change == 0 for the
  // auto-filled case). Keyed on `netPayableBeforeTip` so it fires for ALL base
  // changes, not only when tipMode === 'none' (which left a %-tip chosen before the
  // promo resolved stuck on the pre-discount base). Skipped for split payment and
  // foreign currency, which drive the tender from their own inputs.
  useEffect(() => {
    if (!open || splitMode || selectedCurrency) return;
    if (tipMode === 'pct' && tipPct !== null) {
      const tip = Math.round((netPayableBeforeTip * tipPct / 100) * 100) / 100;
      setTipAmount(tip);
      setAmountTendered(String(netPayableBeforeTip + tip));
    } else if (tipMode === 'custom') {
      setAmountTendered(String(netPayableBeforeTip + tipAmount));
    } else {
      setAmountTendered(String(netPayableBeforeTip));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [netPayableBeforeTip]);

  useEffect(() => {
    if (isOnline && customer?.id && selectedMethod === 'CREDIT') {
      api.get(`/customers/${customer.id}/credit`)
        .then((r) => setCreditInfo(r.data))
        .catch(() => setCreditInfo(null));
    } else {
      setCreditInfo(null);
      // If CREDIT was selected but customer was removed or offline, fall back to CASH
      if (!customer?.id || !isOnline) if (selectedMethod === 'CREDIT') setSelectedMethod('CASH');
    }
  }, [customer?.id, selectedMethod, isOnline]);

  // Fetch loyalty info whenever a customer is selected (only if loyalty addon is active and online)
  useEffect(() => {
    // `open` is a dependency: the open-reset effect clears loyaltyInfo, so the
    // balance must be re-fetched every time the Payment dialog opens (POS-I41).
    if (open && canUseLoyalty && customer?.id && isOnline) {
      api.get(`/customers/${customer.id}/loyalty`)
        .then((r) => setLoyaltyInfo(r.data))
        .catch(() => setLoyaltyInfo(null));
    } else {
      setLoyaltyInfo(null);
      setRedeemPointsInput('');
    }
  }, [open, canUseLoyalty, customer?.id, isOnline]);

  const saleMutation = useMutation({
    // POS-I61: must also run when the browser reports offline, so the sale is queued, not paused.
    ...OFFLINE_CAPABLE_MUTATION,
    mutationFn: async () => {
      const payments = splitMode
        ? splitPayments.map((sp) => ({
            method: sp.method,
            amount: Number(sp.amount),
            reference: sp.reference.trim() || undefined,
            bankAccountId: effectiveBankAccountId(sp.method, sp.bankAccountId, bankAccounts),
            cheque: sp.method === 'CHEQUE' ? chequeFormToPayload(sp.cheque) : undefined,
            giftCardCode: sp.method === 'GIFT_CARD' && sp.giftCardInfo ? sp.giftCardInfo.code : undefined,
          }))
        : [
            {
              method: selectedMethod,
              amount: Number(amountTendered || effectiveGrandTotal),
              reference: reference.trim() || undefined,
              bankAccountId: effectiveBankAccountId(selectedMethod, selectedBankAccountId, bankAccounts),
              cheque: selectedMethod === 'CHEQUE' ? chequeFormToPayload(chequeForm) : undefined,
              giftCardCode: selectedMethod === 'GIFT_CARD' && giftCardInfo ? giftCardInfo.code : undefined,
            },
          ];
      // Remember the last receiving bank account on this device (preselect next time).
      const usedBankId = payments.find((p) => p.bankAccountId)?.bankAccountId;
      if (usedBankId) rememberBankAccountId(usedBankId);
      const salePayload = {
        customerId: customer?.id,
        locationId: selectedLocationId || undefined,
        items: items.map((item) => ({
          productId: item.product.id,
          variantId: item.variant?.id,
          quantity: item.quantity,
          discountType: item.discountType,
          discountValue: item.discountValue,
          // POS-I119: audit trail of a line (bargaining) discount.
          discountReason: item.discountAmount > 0 ? item.discountReason : undefined,
          discountApprovedById: item.discountAmount > 0 ? item.discountApprovedById : undefined,
          serialNumberId: item.serialNumberId ?? undefined,
        })),
        payments,
        discountType: orderDiscountType,
        discountValue: orderDiscountValue,
        notes,
        buyerIsOnAtl,
        couponCode: appliedCoupon?.code ?? undefined,
        redeemPoints: redeemPoints > 0 ? redeemPoints : undefined,
        giftCardCode: selectedMethod === 'GIFT_CARD' && giftCardInfo ? giftCardInfo.code : undefined,
        // Multi-currency
        // Split mode tenders are PKR rows — never tag the sale with a foreign currency (POS-I29)
        currencyCode: !splitMode && selectedCurrency ? selectedCurrency.code : 'PKR',
        foreignAmount: !splitMode && selectedCurrency && foreignAmount ? parseFloat(foreignAmount) : undefined,
        tipAmount: tipAmount > 0 ? tipAmount : undefined,
      };

      if (!isOnline) {
        const localInvoiceNumber = await generateOfflineInvoiceNumber();
        // Cart snapshot for offline receipt rendering — captures everything needed to print
        const cartSnapshot = {
          localInvoiceNumber,
          items,
          customer,
          subtotal,
          totalTax: serverPricing?.totalTax ?? totalTax,
          orderDiscountAmount,
          grandTotal,
          // POS-I62 / POS-I78: what the customer was actually charged, so the offline receipt
          // shows the same lines, promotions, further tax, total and change as the Payment dialog.
          promotionDiscount,
          promotions: promotionPreview.map((p) => ({ name: p.promotionName, discountAmount: p.discountAmount })),
          furtherTaxAmount: serverPricing?.furtherTaxAmount ?? 0,
          taxBreakdown: serverPricing?.taxBreakdown ?? taxBreakdown,
          tipAmount,
          netTotal: netPayableBeforeTip,
          changeAmount: change,
          buyerIsOnAtl,
          payments,
          selectedLocation,
          notes,
          createdAt: new Date().toISOString(),
        };
        const offlineId = await queueOfflineSale(salePayload, cartSnapshot, localInvoiceNumber);
        return {
          id: offlineId,
          invoiceNumber: localInvoiceNumber,
          totalAmount: netPayableBeforeTip,
          _offline: true,
          _cartSnapshot: cartSnapshot,
        } as CheckoutSaleResult;
      }

      return posService.createSale(salePayload);
    },
    onSuccess: (sale: CheckoutSaleResult) => {
      setCompletedChange(sale._offline ? change : Number(sale.changeAmount ?? 0));
      if (sale._offline) {
        toast.success(t('toastSaleQueued'), { icon: <Package className="h-5 w-5" />, duration: 5000 });
      } else {
        toast.success(t('toastSaleCompleted', { invoice: sale.invoiceNumber }));
      }
      // Capture applied promotions before clearing cart
      const promos = sale.salePromotions ?? [];
      setAppliedPromotions(promos.map((sp) => ({
        name: sp.promotion?.name ?? 'Promotion',
        discountAmount: sp.discountAmount,
      })));
      clearCart();
      queryClient.invalidateQueries({ queryKey: ['inventory-products'] });
      queryClient.invalidateQueries({ queryKey: ['tenant-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['pos-products'] });
      queryClient.invalidateQueries({ queryKey: ['sales'] });
      onComplete(sale);
    },
    onError: (error: unknown) => {
      notify.error(error, t('toastSaleFailed'));
    },
  });

  const handleSubmit = (managerApproved = false) => {
        // Check if there are tracked items that need serial selection
        const trackedItems = items.filter((item) => item.product.serialTracking);
        if (trackedItems.length > 0) {
          // Check if all tracked items have a serial selected
          const missingSerials = trackedItems.filter((item) => !item.serialNumberId);
          if (missingSerials.length > 0) {
            toast.error(t('toastSelectSerialFor', { names: missingSerials.map((i) => i.product.name).join(', ') }));
            setShowSerialSelector(true);
            return;
          }
          // Check for duplicate serial selections across line items
          const selectedIds = trackedItems.map((i) => i.serialNumberId).filter(Boolean);
          const uniqueIds = new Set(selectedIds);
          if (uniqueIds.size !== selectedIds.length) {
            toast.error(t('toastDuplicateSerial'));
            setShowSerialSelector(true);
            return;
          }
        }

    // Manager PIN check: require approval if discount exceeds threshold
    if (!managerApproved && managerPinEnabled && grandTotal > 0) {
      if (orderDiscountPct >= managerDiscountThreshold) {
        setManagerApprovalOpen(true);
        return;
      }
    }

    if (splitMode) {
      if (splitPayments.length === 0) { toast.error(t('toastAddPayment')); return; }
      // POS-I32: a surplus over the total is change handed back in cash, so it is accepted when the
      // cash rows can cover it; non-cash tenders must never exceed what they pay for.
      if (splitEval.status === 'short') {
        toast.error(t('toastSplitMustEqual', { split: formatCurrency(splitEval.tendered), total: formatCurrency(effectiveGrandTotal) }));
        return;
      }
      if (splitEval.status === 'noncash_excess') {
        toast.error(t('toastSplitChangeNeedsCash', { excess: formatCurrency(splitEval.excess) }));
        return;
      }
      for (const sp of splitPayments) {
        if (!sp.amount || Number(sp.amount) <= 0) { toast.error(t('toastAmountsPositive')); return; }
        if (paymentNeedsReference(sp.method) && !sp.reference.trim()) {
          toast.error(t('toastReferenceRequiredMethod', { method: sp.method }));
          return;
        }
        if (sp.method === 'CHEQUE') {
          if (!customer?.id) { toast.error(t('selectCustomerForCheque')); return; }
          if (!isChequeFormComplete(sp.cheque)) { toast.error(t('toastChequeDetailsRequired')); return; }
        }
        if (sp.method === 'GIFT_CARD' && !sp.giftCardInfo) {
          toast.error(t('toastLookupGiftCardFirst'));
          return;
        }
        if (sp.method === 'BANK_TRANSFER' && !effectiveBankAccountId(sp.method, sp.bankAccountId, bankAccounts) && bankAccounts.length !== 1) {
          toast.error(t('toastSelectBankForTransfer'));
          return;
        }
      }
      // Credit limit check for split payments
      const totalCreditInSplit = splitPayments
        .filter((sp) => sp.method === 'CREDIT')
        .reduce((sum, sp) => sum + Number(sp.amount || 0), 0);
      if (totalCreditInSplit > 0 && creditInfo) {
        const available = creditInfo.availableCredit ?? (creditInfo.creditLimit != null ? creditInfo.creditLimit - creditInfo.outstandingBalance : null);
        if (available != null && totalCreditInSplit > available) {
          toast.error(t('toastInsufficientCreditAvail', { amount: formatCurrency(available) }));
          return;
        }
      }
      saleMutation.mutate();
      return;
    }
    const tendered = Number(amountTendered);
    if (isNaN(tendered) || tendered <= 0) {
      toast.error(t('toastValidAmount'));
      return;
    }
    if (tendered < effectiveGrandTotal) {
      toast.error(t('toastInsufficientPayment'));
      return;
    }
    // Reference required for card/wallet (not cash, credit, gift card, QR, cheque, bank transfer)
    if (paymentNeedsReference(selectedMethod) && !reference.trim()) {
      toast.error(t('toastRefRequiredNonCash'));
      return;
    }
    if (selectedMethod === 'CHEQUE') {
      if (!customer?.id) { toast.error(t('selectCustomerForCheque')); return; }
      if (!isChequeFormComplete(chequeForm)) { toast.error(t('toastChequeDetailsRequired')); return; }
    }
    // Gift card must be looked up first
    if (selectedMethod === 'GIFT_CARD' && !giftCardInfo) {
      toast.error(t('toastGiftCardFirst'));
      return;
    }
    // Bank account required for BANK_TRANSFER
    if (selectedMethod === 'BANK_TRANSFER' && !effectiveBankAccountId(selectedMethod, selectedBankAccountId, bankAccounts) && bankAccounts.length !== 1) {
      toast.error(t('toastSelectBankForTransfer'));
      return;
    }
    // Credit limit validation
    if (selectedMethod === 'CREDIT' && creditInfo) {
      const available = creditInfo.availableCredit ?? (creditInfo.creditLimit != null ? creditInfo.creditLimit - creditInfo.outstandingBalance : null);
      if (available != null && effectiveGrandTotal > available) {
        toast.error(t('toastInsufficientCreditAvail', { amount: formatCurrency(available) }));
        return;
      }
    }
    saleMutation.mutate();
  };

  // POS-I59: quick cash checkout. Once the dialog is open with Cash and the exact amount, the server
  // total has settled and nothing else is pending, the sale completes without another key press.
  // Any validation failure (serials, manager approval, ...) shows its usual message/dialog and
  // leaves the Payment dialog open for the cashier.
  const quickFiredRef = useRef(false);
  useEffect(() => {
    if (!open) { quickFiredRef.current = false; return; }
    if (!quickCash || quickFiredRef.current) return;
    if (splitMode || selectedMethod !== 'CASH' || selectedCurrency || saleMutation.isPending) return;
    if (loyaltyPreviewPending || managerApprovalOpen) return;
    if (!(effectiveGrandTotal > 0) || Number(amountTendered) !== effectiveGrandTotal) return;
    const timer = setTimeout(() => {
      quickFiredRef.current = true;
      handleSubmit();
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, quickCash, splitMode, selectedMethod, selectedCurrency, loyaltyPreviewPending, managerApprovalOpen, effectiveGrandTotal, amountTendered]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !saleMutation.isPending) {
      handleSubmit();
    }
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponLoading(true);
    try {
      const res = await api.get(`/promotions/by-code/${encodeURIComponent(couponCode.trim())}`);
      setAppliedCoupon(res.data);
      toast.success(t('toastCouponApplied', { name: res.data.name }));
      setCouponCode('');
    } catch (err: unknown) {
      notify.error(err, t('toastCouponInvalid'));
    } finally {
      setCouponLoading(false);
    }
  };

  const handleLookupGiftCard = async () => {
    if (!giftCardCode.trim()) return;
    setGiftCardLoading(true);
    try {
      const res = await api.get(`/gift-cards/${encodeURIComponent(giftCardCode.trim())}`);
      if (!res.data.isActive) {
        toast.error(t('toastGiftCardInactiveVoided'));
        return;
      }
      setGiftCardInfo({ currentBalance: Number(res.data.currentBalance), code: res.data.code });
      setAmountTendered(String(Math.min(Number(res.data.currentBalance), effectiveGrandTotal)));
      toast.success(t('toastGiftCardFound', { amount: formatCurrency(Number(res.data.currentBalance)) }));
    } catch (err: unknown) {
      notify.error(err, t('toastGiftCardNotFound'));
    } finally {
      setGiftCardLoading(false);
    }
  };

  // Quick cash buttons
  const quickAmounts = [
    effectiveGrandTotal,
    Math.ceil(effectiveGrandTotal / 100) * 100,
    Math.ceil(effectiveGrandTotal / 500) * 500,
    Math.ceil(effectiveGrandTotal / 1000) * 1000,
  ].filter((v, i, arr) => arr.indexOf(v) === i && v >= effectiveGrandTotal);

  return (
    <>
      <Dialog
        visible={open}
        onHide={onClose}
        modal
        dismissableMask
        showHeader={false}
        style={{ width: '56rem', maxWidth: '100%' }}
        contentStyle={{ padding: 0, borderRadius: '1rem', overflow: 'hidden' }}
        pt={{ root: { className: 'rounded-2xl border shadow-2xl' } }}
      >
      <div className="bg-card rounded-2xl flex flex-col overflow-hidden" style={{ maxHeight: '92vh' }} onKeyDown={handleKeyDown}>
        {saleMutation.isSuccess ? (
          <div className="flex flex-col items-center p-8">
            <CheckCircle2 className="h-16 w-16 text-success-text mb-4 animate-scale-in" />
            <h2 className="text-2xl font-bold">{t('saleComplete')}</h2>
            {completedChange > 0 && (
              <p className="mt-2 text-lg">
                {t('change')}: <span className="font-bold text-primary-text">{formatCurrency(completedChange)}</span>
              </p>
            )}
            {appliedPromotions.length > 0 && (
              <div className="mt-3 w-full rounded-lg bg-success/10 border border-success/15 px-4 py-2.5 text-sm">
                <p className="font-semibold text-success-text mb-1 flex items-center gap-1">
                  <Tag className="h-4 w-4" /> {t('promotionsApplied')}
                </p>
                {appliedPromotions.map((p, i) => (
                  <div key={i} className="flex justify-between text-success-text text-xs">
                    <span>{p.name}</span>
                    <span>-{formatCurrency(Number(p.discountAmount))}</span>
                  </div>
                ))}
              </div>
            )}
            <Button className="mt-6 bg-gradient-to-r from-primary via-primary to-primary text-primary-foreground shadow-md" onClick={() => { saleMutation.reset(); onClose(); }}>
              {t('newSale')}
            </Button>
          </div>
        ) : (
          <div className="flex flex-col flex-1 overflow-hidden">
            <div className="flex-1 overflow-y-auto p-6 pb-8 scroll-pb-8">
            <h2 className="text-xl font-bold mb-1">{t('payment')}</h2>
            <p className="text-3xl font-bold text-primary mb-6">
              {formatCurrency(effectiveGrandTotal)}
              {hasPaymentMethodTax && effectiveGrandTotal !== grandTotal && (
                <span className="ms-2 text-base font-normal line-through text-muted-foreground">{formatCurrency(grandTotal)}</span>
              )}
            </p>
            {selectedCurrency && !splitMode && (
              <p className="-mt-4 mb-6 text-sm font-medium text-muted-foreground">
                {t('totalInCurrency', { code: selectedCurrency.code })}: <bdi dir="ltr">{selectedCurrency.code} {foreignDueText(selectedCurrency, effectiveGrandTotal)}</bdi>
              </p>
            )}

            {/* Offline mode banner */}
            {!isOnline && (
              <div className="mb-4 rounded-lg border border-warning/30 bg-warning/10 px-3 py-2.5 text-sm text-warning-text flex items-center gap-2">
                <WifiOff className="h-4 w-4 shrink-0 text-warning-text" />
                <span>
                  <span className="font-semibold">{t('offlineModeTitle')}</span> — {t('offlineModeHint')}
                </span>
              </div>
            )}
            {items.some((item) => item.product.serialTracking && !item.serialNumberId) && (
              <div className="mb-4 rounded-lg border border-warning/30 bg-warning/10 px-3 py-2.5 text-xs text-warning-text">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-warning-text" />
                    <span className="font-semibold">
                      {serialFetchError ? t('serialLoadError') : t('serialRequiredClickSelect')}
                    </span>
                  </div>
                  {!serialFetchError && (
                    <button
                      onClick={() => setShowSerialSelector(true)}
                      className="min-h-[44px] rounded-md bg-warning px-4 text-sm font-medium text-warning-foreground hover:bg-warning/90 transition-colors"
                    >
                      {t('selectSerials')}
                    </button>
                  )}
                </div>
              </div>
            )}
            {items.some((item) => item.product.serialTracking && item.serialNumberId) &&
              !items.some((item) => item.product.serialTracking && !item.serialNumberId) && (
              <div className="mb-4 rounded-lg border border-success/30 bg-success/10 px-3 py-2.5 text-xs text-success-text flex items-center gap-2">
                <CheckCircle2 className="h-3.5 w-3.5 text-success-text shrink-0" />
                <span>{t('allSerialsSelected')}</span>
                <button onClick={() => setShowSerialSelector(true)} className="ms-auto underline hover:no-underline text-success-text">{t('changeAction')}</button>
              </div>
            )}

            {/* FBR pre-flight warning */}
            {canUseFbr && fbrActive && missingHsCodeItems.length > 0 && !fbrWarningDismissed && (
              <div className="mb-4 rounded-lg border border-warning/30 bg-warning/10 px-3 py-2.5 text-xs text-warning-text">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-warning-text" />
                    <div>
                      <p className="font-semibold">{t('fbrSubmissionWarning')}</p>
                      <p className="mt-0.5">
                        {missingHsCodeItems.length === 1
                          ? t('fbrMissingHsCodeOne', { name: missingHsCodeItems[0].product.name })
                          : t('fbrMissingHsCodeMany', { count: missingHsCodeItems.length })}
                        {' '}{t('fbrMissingHsCodeHint')}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setFbrWarningDismissed(true)}
                    className="shrink-0 text-warning-text hover:text-warning-text transition-colors"
                    aria-label={t('dismiss')}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}

            <div className="mb-5 rounded-lg border bg-muted/30 p-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('subtotalPreTax')}</span>
                <span>{formatCurrency(netEligibleBase)}</span>
              </div>
              {orderDiscountAmount > 0 && (
                <div className="flex justify-between text-success-text">
                  <span>{t('discount')}</span>
                  <span>-{formatCurrency(orderDiscountAmount)}</span>
                </div>
              )}
              {promotionDiscount > 0 && (
                <div className="flex justify-between text-success-text">
                  <span className="flex items-center gap-1"><Gift className="h-3.5 w-3.5" /> {t('promotionsApplied')}</span>
                  <span>-{formatCurrency(promotionDiscount)}</span>
                </div>
              )}
              {/* Non-blocking warning: the promo preview errored, so the Total below
                  may not reflect an applicable discount. Surfacing it keeps a failed
                  check visible instead of silently charging the pre-discount total. (T-369) */}
              {previewFailed && (
                <div className="flex items-start gap-1.5 text-warning-text text-xs mt-1">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                  <span>{t('promoPreviewFailed')}</span>
                </div>
              )}
              {/* Loyalty redemption — server-applied (pre-tax capped + whole-point quantized),
                  so the Total below already nets it out, matching the committed sale. (T-370) */}
              {redeemPoints > 0 && !loyaltyPreviewFailed && appliedLoyaltyDiscount > 0 && (
                <div className="flex justify-between text-success-text">
                  <span className="flex items-center gap-1"><Award className="h-3.5 w-3.5" /> {t('loyaltyRedemption')}</span>
                  <span>-{formatCurrency(appliedLoyaltyDiscount)}</span>
                </div>
              )}
              {/* First-load cue: no redemption discount to hold steady yet, so say the Total is
                  still being computed instead of showing the un-redeemed amount silently. (T-375) */}
              {redeemPoints > 0 && !loyaltyPreviewFailed && loyaltyPreviewPending && (
                <div className="flex items-center gap-1.5 text-muted-foreground text-xs mt-1" role="status">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  <span>{t('previewCalculating')}</span>
                </div>
              )}
              {/* Non-blocking warning: the loyalty preview errored, so the Total is NOT reduced
                  by the requested points here (the server still caps on commit). (T-370) */}
              {redeemPoints > 0 && loyaltyPreviewFailed && (
                <div className="flex items-start gap-1.5 text-warning-text text-xs mt-1">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                  <span>{t('loyaltyPreviewFailed')}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  {t('tax')} {hasPaymentMethodTax && currentMethodRate !== null ? `(${currentMethodRate}% — ${pmTaxRules?.[selectedMethod]?.name ?? selectedMethod})` : ''}
                </span>
                <span>{formatCurrency(serverPricing && !hasPaymentMethodTax ? serverPricing.totalTax : effectiveTax)}</span>
              </div>
              {serverPricing && !hasPaymentMethodTax && serverPricing.taxBreakdown.map((tax) => (
                <div key={`${tax.name}-${tax.rate}-${tax.type}`} className="flex justify-between text-xs text-muted-foreground">
                  <span>{formatTaxLabel(tax)}</span>
                  <span>{formatCurrency(tax.amount)}</span>
                </div>
              ))}
              {!serverPricing && !hasPaymentMethodTax && taxBreakdown.map((tax) => (
                <div key={`${tax.name}-${tax.rate}-${tax.type}`} className="flex justify-between text-xs text-muted-foreground">
                  <span>{formatTaxLabel(tax)}</span>
                  <span>{formatCurrency(tax.amount)}</span>
                </div>
              ))}
              <div className="mt-2 flex justify-between border-t pt-2 text-xl font-bold">
                <span>{t('total')}</span>
                <span>{formatCurrency(effectiveGrandTotal - tipAmount)}</span>
              </div>
              {/* Tip section */}
              <div className="mt-2 border-t pt-2">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-medium text-muted-foreground">{t('tipGratuity')}</span>
                  {tipAmount > 0 && (
                    <span className="text-xs font-semibold text-success-text">{formatCurrency(tipAmount)}</span>
                  )}
                </div>
                <div className="flex gap-1.5 flex-wrap">
                  {[5, 10, 15].map((pct) => (
                    <button
                      key={pct}
                      type="button"
                      onClick={() => {
                        // Tip base = net payable AFTER promotion (SSOT). (T-356)
                        // Remember the pct so the resync effect can recompute the tip
                        // on the net base if it later moves (promo/pm-tax). (T-367)
                        const tip = Math.round((netPayableBeforeTip * pct / 100) * 100) / 100;
                        setTipPct(pct);
                        setTipAmount(tip);
                        setTipCustomInput('');
                        setTipMode('pct');
                        setAmountTendered(String(netPayableBeforeTip + tip));
                      }}
                      className={`min-h-[44px] px-4 rounded text-sm font-medium border transition-colors ${tipMode === 'pct' && Math.abs(tipAmount - Math.round((netPayableBeforeTip * pct / 100) * 100) / 100) < 0.01 ? 'bg-primary text-primary-foreground border-primary' : 'bg-muted hover:bg-muted/80 border-border'}`}
                    >
                      {pct}%
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      // Switching to Custom clears any % tip and resyncs the tender to
                      // the net (tip 0) so no phantom change lingers until a value is
                      // typed. (T-367)
                      setTipMode('custom');
                      setTipCustomInput('');
                      setTipAmount(0);
                      setTipPct(null);
                      setAmountTendered(String(netPayableBeforeTip));
                    }}
                    className={`min-h-[44px] px-4 rounded text-sm font-medium border transition-colors ${tipMode === 'custom' ? 'bg-primary text-primary-foreground border-primary' : 'bg-muted hover:bg-muted/80 border-border'}`}
                  >
                    {t('tipCustom')}
                  </button>
                  {tipAmount > 0 && (
                    <button
                      type="button"
                      // Remove must also resync the tender back to the net, otherwise
                      // the old tip stays in amountTendered as phantom change. (T-367)
                      onClick={() => { setTipAmount(0); setTipCustomInput(''); setTipMode('none'); setTipPct(null); setAmountTendered(String(netPayableBeforeTip)); }}
                      className="min-h-[44px] px-4 rounded text-sm font-medium border border-border bg-muted hover:bg-muted/80 text-destructive-text"
                    >
                      {t('remove')}
                    </button>
                  )}
                </div>
                {tipMode === 'custom' && (
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder={t('enterTipAmount')}
                    value={tipCustomInput}
                    onChange={(e) => {
                      setTipCustomInput(e.target.value);
                      const val = parseFloat(e.target.value) || 0;
                      setTipAmount(Math.max(0, val));
                      // Tender = net payable AFTER promotion + tip (SSOT). (T-356)
                      setAmountTendered(String(netPayableBeforeTip + Math.max(0, val)));
                    }}
                    className="mt-1.5 w-full h-11 rounded-md border bg-background px-3 text-sm"
                    autoFocus
                  />
                )}
              </div>
              {tipAmount > 0 && (
                <div className="mt-1 flex justify-between border-t pt-1 font-bold text-base">
                  <span>{t('grandTotalInclTip')}</span>
                  <span>{formatCurrency(effectiveGrandTotal)}</span>
                </div>
              )}
              {/* Loyalty earn preview — only show when loyalty addon is active */}
              {canUseLoyalty && customer && (
                <div className="mt-1 flex justify-between text-xs text-warning-text">
                  <span className="flex items-center gap-1"><Award className="h-3.5 w-3.5" /> {t('pointsToEarn')}</span>
                  <span>{t('pointsEarnedValue', { points: serverPricing?.loyaltyPointsToEarn ?? Math.floor(Math.max(0, netPayableBeforeTip) / 10) })}</span>
                </div>
              )}
            </div>

            {/* Store / Location selector */}
            {locationsData && locationsData.length > 0 && (
              <div className="mb-4">
                <Label className="flex items-center gap-1.5 mb-1.5">
                  <MapPin className="h-3.5 w-3.5 text-primary-text" /> {t('storeLocation')}
                  {(locationsData.length === 1 || !!user?.locationId) && (
                    <span className="ms-auto text-xs text-muted-foreground font-normal">{t('autoSelected')}</span>
                  )}
                </Label>
                <Dropdown
                  value={selectedLocationId}
                  onChange={(e) => setSelectedLocationId(e.value as string)}
                  disabled={locationsData.length === 1 || !!user?.locationId}
                  options={[
                    { label: t('noLocationOption'), value: '' },
                    ...locationsData.map((loc) => ({
                      label: `${loc.name}${loc.isDefault ? ` (${t('defaultSuffix')})` : ''}${loc.paymentMethodTaxEnabled ? ` • ${t('methodTaxSuffix')}` : ''}${loc.atlEnabled ? ` • ${t('atlRulesSuffix')}` : ''}`,
                      value: loc.id,
                    })),
                  ]}
                  optionLabel="label"
                  optionValue="value"
                  className="w-full"
                />
                {/* Show active overrides for the selected location */}
                {selectedLocation && (selectedLocation.paymentMethodTaxEnabled || selectedLocation.atlEnabled || selectedLocation.fbrSroScheduleNo) && (
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {selectedLocation.paymentMethodTaxEnabled && (
                      <span className="inline-flex items-center rounded-full bg-info/10 px-2 py-0.5 text-xs font-medium text-info-text">
                        {t('branchTaxActive')}
                      </span>
                    )}
                    {selectedLocation.atlEnabled && (
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${selectedLocation.defaultBuyerAtlStatus ? 'bg-success/10 text-success-text' : 'bg-warning/10 text-warning-text'}`}>
                        {selectedLocation.defaultBuyerAtlStatus ? t('defaultAtl') : t('furtherTaxRate', { rate: selectedLocation.furtherTaxRate })}
                      </span>
                    )}
                    {selectedLocation.fbrSroScheduleNo && (
                      <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary-text">
                        {t('sroLabel')} {selectedLocation.fbrSroScheduleNo}
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Currency selector — only shown when multi-currency addon is active and there are active non-PKR currencies */}
            {canUseMultiCurrency && currencies.filter((c) => c.code !== 'PKR' && c.isActive).length > 0 && (
              <div className="mb-4">
                <Label className="flex items-center gap-1.5 mb-1.5 text-sm font-medium">
                  {t('foreignCurrencyOptional')}
                </Label>
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={() => { autoForeignRef.current = false; setSelectedCurrency(null); setForeignAmount(''); setAmountTendered(String(effectiveGrandTotal)); }}
                    className={`min-h-[44px] rounded-lg border px-4 text-sm font-medium transition-colors ${!selectedCurrency ? 'border-primary bg-primary text-primary-foreground' : 'border-border hover:bg-muted'}`}
                  >
                    Rs PKR
                  </button>
                  {currencies.filter((c) => c.code !== 'PKR' && c.isActive).map((c) => (
                    <button
                      key={c.code}
                      onClick={() => { autoForeignRef.current = !splitMode && selectedMethod === 'CASH'; setSelectedCurrency(c); setForeignAmount(''); }}
                      className={`min-h-[44px] rounded-lg border px-4 text-sm font-medium transition-colors ${selectedCurrency?.code === c.code ? 'border-primary bg-primary text-primary-foreground' : 'border-border hover:bg-muted'}`}
                    >
                      {c.symbol} {c.code}
                    </button>
                  ))}
                </div>

                {selectedCurrency && splitMode && (
                  <p className="mt-2 text-xs text-warning-text">
                    <AlertTriangle className="inline h-3.5 w-3.5 me-1 align-text-bottom" />
                    {t('foreignCurrencySplitNote')}
                  </p>
                )}
                {selectedCurrency && !splitMode && (
                  <div ref={foreignBoxRef} className="mt-2 rounded-lg border border-info/30 bg-info/10 px-3 py-2 space-y-1.5">
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">{t('rateLabel')}</span>
                      <span>1 {selectedCurrency.code} = Rs {formatRate(selectedCurrency.exchangeRate)}</span>
                    </div>
                    <div className="flex justify-between text-xs font-medium">
                      <span className="text-muted-foreground">{t('foreignDue')}</span>
                      <span>
                        {selectedCurrency.code}{' '}
                        {(effectiveGrandTotal / (Number(selectedCurrency.exchangeRate) || 1)).toFixed(2)}
                      </span>
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-muted-foreground mb-1 block">
                        {t('amountIn', { currency: selectedCurrency.code })}
                      </Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder={`${selectedCurrency.symbol} 0.00`}
                        value={foreignAmount}
                        onChange={(e) => {
                          autoForeignRef.current = false;
                          setForeignAmount(e.target.value);
                          const pkrEquiv = parseFloat(e.target.value) * Number(selectedCurrency.exchangeRate);
                          if (!isNaN(pkrEquiv) && pkrEquiv > 0) {
                            setAmountTendered(pkrEquiv.toFixed(2));
                          }
                        }}
                        className="h-11 text-sm"
                      />
                    </div>
                    <div className="flex justify-between text-xs font-medium">
                      <span className="text-muted-foreground">{t('pkrEquivalent')}</span>
                      <span className="text-info-text">
                        {formatMoney2(Number(amountTendered) || 0)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Payment method selection + Split toggle */}
            <div className="mb-2 flex items-center gap-2">
              <span className="text-sm font-medium">{t('paymentMethod')}</span>
              <button
                onClick={() => {
                  setSplitMode((v) => {
                    if (!v) setSplitPayments([{ id: generateId(), method: 'CASH', amount: String(effectiveGrandTotal), reference: '', bankAccountId: '', cheque: emptyChequeForm(customer?.name ?? ''), giftCardCode: '', giftCardInfo: null }]);
                    else setSplitPayments([]);
                    return !v;
                  });
                }}
                className={`ms-auto flex min-h-[44px] items-center gap-1.5 rounded-md border px-3 text-sm font-medium transition-colors ${splitMode ? 'bg-primary/10 border-primary/30 text-primary-text' : 'bg-background text-muted-foreground hover:bg-accent hover:text-foreground'}`}
              >
                <SplitSquareHorizontal className="h-3.5 w-3.5" />
                {t('splitPayment')}
              </button>
            </div>

            {splitMode ? (
              /* ── Split payment mode ── */
              <div className="mb-5 space-y-2">
                {splitPayments.map((sp, idx) => {
                  return (
                    <div key={sp.id} className="rounded-lg border p-3 space-y-2 bg-muted/20">
                      <div className="flex items-center gap-2">
                        <Dropdown
                          value={sp.method}
                          onChange={(e) => setSplitPayments((prev) => prev.map((s, i) => i === idx ? { ...s, method: e.value as PaymentMethod } : s))}
                          options={availablePaymentMethods.map((m) => ({ label: m.label, value: m.method, disabled: (m.method === 'CREDIT' || m.method === 'CHEQUE') && !customer?.id }))}
                          optionLabel="label"
                          optionValue="value"
                          optionDisabled="disabled"
                          className="flex-1"
                        />
                        <Input
                          type="number"
                          min={0.01}
                          step={0.01}
                          value={sp.amount}
                          onChange={(e) => setSplitPayments((prev) => prev.map((s, i) => i === idx ? { ...s, amount: e.target.value } : s))}
                          className="h-11 w-28 text-end"
                          placeholder={sp.method === 'CASH' ? t('cashReceivedPlaceholder') : t('amountLabel')}
                        />
                        {splitPayments.length > 1 && (
                          <button aria-label={t('remove')} onClick={() => setSplitPayments((prev) => prev.filter((_, i) => i !== idx))} className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded hover:bg-destructive/10">
                            <Trash2 className="h-4 w-4 text-destructive-text" />
                          </button>
                        )}
                      </div>
                      {sp.method !== 'CASH' && sp.method !== 'CREDIT' && sp.method !== 'GIFT_CARD' && sp.method !== 'CHEQUE' && (
                        <Input
                          value={sp.reference}
                          onChange={(e) => setSplitPayments((prev) => prev.map((s, i) => i === idx ? { ...s, reference: e.target.value } : s))}
                          placeholder={paymentNeedsReference(sp.method) ? t('transactionRef') : `${t('transactionRef')} (${t('optionalLabel')})`}
                          className="h-11 text-sm"
                        />
                      )}
                      {sp.method === 'GIFT_CARD' && (
                        sp.giftCardInfo ? (
                          <div className="flex items-center justify-between rounded-md bg-primary/10 border border-primary/30 px-2.5 py-1.5 text-xs">
                            <span className="font-mono text-primary-text">{sp.giftCardInfo.code}</span>
                            <span className="font-semibold text-primary-text">{t('balanceShort', { amount: formatCurrency(sp.giftCardInfo.currentBalance) })}</span>
                            <button onClick={() => setSplitPayments((prev) => prev.map((s, i) => i === idx ? { ...s, giftCardInfo: null, giftCardCode: '', amount: '' } : s))} className="text-destructive-text hover:underline ms-2">{t('remove')}</button>
                          </div>
                        ) : (
                          <div className="flex gap-2">
                            <Input
                              placeholder={t('enterGiftCardCode')}
                              value={sp.giftCardCode}
                              onChange={(e) => setSplitPayments((prev) => prev.map((s, i) => i === idx ? { ...s, giftCardCode: e.target.value } : s))}
                              className="h-11 text-sm font-mono flex-1"
                            />
                            <Button size="sm" variant="secondary" className="min-h-[44px] text-sm shrink-0"
                              disabled={!sp.giftCardCode.trim()}
                              onClick={async () => {
                                try {
                                  const res = await api.get(`/gift-cards/${encodeURIComponent(sp.giftCardCode.trim())}`);
                                  if (!res.data.isActive) { toast.error(t('toastGiftCardInactive')); return; }
                                  const bal = Number(res.data.currentBalance);
                                  setSplitPayments((prev) => prev.map((s, i) => i === idx ? { ...s, giftCardInfo: { currentBalance: bal, code: res.data.code }, amount: String(bal) } : s));
                                  toast.success(t('toastGiftCardBalance', { amount: formatCurrency(bal) }));
                                } catch (err: unknown) { notify.error(err, t('toastGiftCardNotFound')); }
                              }}>
                              {t('lookup')}
                            </Button>
                          </div>
                        )
                      )}
                      <BankAccountField
                        method={sp.method}
                        accounts={bankAccounts}
                        value={sp.bankAccountId}
                        onChange={(id) => setSplitPayments((prev) => prev.map((s, i) => i === idx ? { ...s, bankAccountId: id } : s))}
                      />
                      {sp.method === 'CHEQUE' && (
                        <ChequeFields
                          value={sp.cheque}
                          onChange={(next) => setSplitPayments((prev) => prev.map((s, i) => i === idx ? { ...s, cheque: next } : s))}
                        />
                      )}
                    </div>
                  );
                })}
                <button
                  onClick={() => setSplitPayments((prev) => [...prev, { id: generateId(), method: 'CASH', amount: '', reference: '', bankAccountId: '', cheque: emptyChequeForm(customer?.name ?? ''), giftCardCode: '', giftCardInfo: null }])}
                  className="flex items-center gap-1.5 rounded-md border border-dashed px-3 py-2 text-xs text-muted-foreground hover:bg-accent w-full justify-center"
                >
                  <Plus className="h-3.5 w-3.5" />
                  {t('addPayment')}
                </button>
                {/* Remaining balance indicator */}
                {(() => {
                  const remaining = effectiveGrandTotal - splitEval.tendered;
                  // POS-I32: cash tendered above the total is shown as change to give back.
                  if (splitEval.status === 'change') {
                    return (
                      <div className="flex justify-between text-sm font-medium px-1 text-success-text" data-testid="split-change-due">
                        <span>{t('splitChangeDue')}</span>
                        <span>{formatCurrency(splitEval.change)}</span>
                      </div>
                    );
                  }
                  return (
                    <div className={`flex justify-between text-sm font-medium px-1 ${splitEval.status === 'exact' ? 'text-success-text' : splitEval.status === 'short' ? 'text-warning-text' : 'text-destructive-text'}`}>
                      <span>{t('remaining')}</span>
                      <span>{formatCurrency(remaining)}</span>
                    </div>
                  );
                })()}
              </div>
            ) : (
              /* ── Single payment mode ── */
              <>
                <div className={`grid gap-2 mb-4 ${availablePaymentMethods.length <= 5 ? 'grid-cols-5' : 'grid-cols-6'}`}>
                  {availablePaymentMethods.map(({ method, label, icon }) => {
                    const isDisabled = (method === 'CREDIT' || method === 'CHEQUE') && !customer;
                    return (
                    <button
                      key={method}
                      onClick={() => !isDisabled && setSelectedMethod(method)}
                      title={isDisabled ? (method === 'CHEQUE' ? t('selectCustomerForCheque') : t('selectCustomerForCredit')) : undefined}
                      disabled={isDisabled}
                      className={`flex flex-col items-center justify-center gap-1 rounded-lg border p-2.5 text-sm transition-all min-h-[68px] ${
                        isDisabled
                          ? 'opacity-40 cursor-not-allowed border-border'
                          : selectedMethod === method
                          ? 'border-primary bg-gradient-to-r from-primary/10 to-primary/10 text-primary-text'
                          : 'hover:border-primary/50'
                      }`}
                    >
                      {icon}
                      <span className="text-sm font-medium leading-tight text-center">{label}</span>
                      {!isDisabled && hasPaymentMethodTax && pmTaxRules?.[method] && (
                        <span className={`text-xs font-semibold ${selectedMethod === method ? 'text-primary-text' : 'text-muted-foreground'}`}>
                          {pmTaxRules[method].rate}%
                        </span>
                      )}
                      {isDisabled && (
                        <span className="text-xs text-muted-foreground leading-tight text-center">{t('noCustomerShort')}</span>
                      )}
                    </button>
                    );
                  })}
                </div>

                {/* Credit limit info */}
                {selectedMethod === 'CREDIT' && customer && creditInfo && (
                  <div className={`mb-4 rounded-lg border px-3 py-2 text-sm ${
                    creditInfo.availableCredit !== null && effectiveGrandTotal > creditInfo.availableCredit
                      ? 'border-destructive/30 bg-destructive/10'
                      : 'border-info/30 bg-info/10'
                  }`}>
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">{t('creditLimitLabel')}</span>
                      <span>{creditInfo.creditLimit !== null ? formatCurrency(creditInfo.creditLimit) : t('unlimited')}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">{t('outstandingLabel')}</span>
                      <span>{formatCurrency(creditInfo.outstandingBalance)}</span>
                    </div>
                    <div className="flex justify-between text-xs font-medium">
                      <span className="text-muted-foreground">{t('availableLabel')}</span>
                      <span className={creditInfo.availableCredit !== null && effectiveGrandTotal > creditInfo.availableCredit ? 'text-destructive-text' : 'text-success-text'}>
                        {creditInfo.availableCredit !== null ? formatCurrency(creditInfo.availableCredit) : t('unlimited')}
                      </span>
                    </div>
                    {creditInfo.availableCredit !== null && effectiveGrandTotal > creditInfo.availableCredit && (
                      <p className="mt-1 text-xs text-destructive-text font-medium"><AlertTriangle className="inline h-3.5 w-3.5 me-1 align-text-bottom" />{t('insufficientCreditSale')}</p>
                    )}
                  </div>
                )}

                {/* Gift Card lookup */}
                {selectedMethod === 'GIFT_CARD' && (
                  <div className="mb-4 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2.5 space-y-2">
                    <p className="text-xs font-semibold text-primary-text flex items-center gap-1.5">
                      <Gift className="h-3.5 w-3.5" /> {t('giftCard')}
                    </p>
                    {giftCardInfo ? (
                      <div className="space-y-1">
                        <div className="flex justify-between text-xs">
                          <span className="text-muted-foreground">{t('cardLabel')}</span>
                          <span className="font-mono font-medium">{giftCardInfo.code}</span>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span className="text-muted-foreground">{t('balanceLabel')}</span>
                          <span className="font-semibold text-primary-text">{formatCurrency(giftCardInfo.currentBalance)}</span>
                        </div>
                        {giftCardInfo.currentBalance < effectiveGrandTotal && (
                          <p className="text-xs text-warning-text"><AlertTriangle className="inline h-3.5 w-3.5 me-1 align-text-bottom" />{t('giftCardInsufficient')}</p>
                        )}
                        <button onClick={() => { setGiftCardInfo(null); setGiftCardCode(''); }} className="inline-flex min-h-[44px] items-center text-xs text-destructive-text hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">{t('removeCard')}</button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <Input
                          placeholder={t('enterGiftCardCode')}
                          value={giftCardCode}
                          onChange={(e) => setGiftCardCode(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleLookupGiftCard()}
                          className="flex-1 h-11 text-sm font-mono"
                        />
                        <Button size="sm" variant="secondary" onClick={handleLookupGiftCard} disabled={giftCardLoading || !giftCardCode.trim()} className="min-h-[44px] text-sm">
                          {giftCardLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : t('lookup')}
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* Tax savings badge when payment method tax rules are active */}
                {hasPaymentMethodTax && (
                  <div className={`mb-4 rounded-lg px-3 py-2 text-sm ${taxSavings > 0 ? 'bg-success/10 border border-success/15' : 'bg-muted/40 border'}`}>
                    {taxSavings > 0 ? (
                      <p className="text-success-text font-medium text-xs">
                        <PartyPopper className="inline h-4 w-4 me-1 align-text-bottom" />{t('taxSavingsBadge', { amount: formatCurrency(taxSavings), name: pmTaxRules?.[selectedMethod]?.name, rate: currentMethodRate })}
                      </p>
                    ) : (
                      <p className="text-muted-foreground text-xs">
                        {pmTaxRules?.[selectedMethod]?.name ?? t('tax')}: {currentMethodRate}%
                      </p>
                    )}
                  </div>
                )}
              </>
            )}

            {/* ATL Buyer toggle — only shown when ATL is enabled at location or tenant level */}
            {effectiveAtlEnabled && !hasPaymentMethodTax && (
            <div className="mb-5 rounded-lg border p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">{t('buyerAtlStatus')}</p>
                  <p className="text-xs text-muted-foreground">
                    {buyerIsOnAtl ? t('atlRegistered') : t('atlUnregistered')}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setBuyerIsOnAtl(false)}
                    className={`flex min-h-[44px] items-center gap-1 rounded-md px-3 text-sm font-medium border transition-colors ${!buyerIsOnAtl ? 'bg-warning/10 border-warning/30 text-warning-text' : 'border-transparent hover:bg-muted/30 text-muted-foreground'}`}
                  >
                    <UserX className="h-3 w-3" /> {t('nonAtl')}
                  </button>
                  <button
                    onClick={() => setBuyerIsOnAtl(true)}
                    className={`flex min-h-[44px] items-center gap-1 rounded-md px-3 text-sm font-medium border transition-colors ${buyerIsOnAtl ? 'bg-success/10 border-success/30 text-success-text' : 'border-transparent hover:bg-muted/30 text-muted-foreground'}`}
                  >
                    <UserCheck className="h-3 w-3" /> {t('atl')}
                  </button>
                </div>
              </div>
            </div>
            )}

            {/* Customer purchase history */}
            {customer && customerHistory && customerHistory.length > 0 && (
              <div className="mb-4 rounded-lg border bg-muted/20 p-3">
                <p className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5 mb-2">
                  <History className="h-3.5 w-3.5" />
                  {t('customerLastPurchases', { name: customer.name, count: customerHistory.length })}
                </p>
                <div className="space-y-1.5">
                  {customerHistory.map((sale) => (
                    <div key={sale.id} className="flex items-center justify-between text-xs">
                      <span className="flex items-center gap-1.5 text-muted-foreground">
                        <ShoppingBag className="h-3 w-3 shrink-0" />
                        {sale.invoiceNumber}
                        <span className="text-muted-foreground/60">
                          · {new Date(sale.createdAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}
                        </span>
                      </span>
                      <span className="font-semibold">{formatCurrency(Number(sale.grandTotal ?? sale.totalAmount ?? 0))}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Loyalty Points */}
            {canUseLoyalty && loyaltyInfo && loyaltyInfo.loyaltyPoints > 0 && (
              <div className="pb-2">
                <div className="rounded-lg border border-warning/20 bg-warning/5 px-3 py-2.5 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-1.5 text-sm font-medium text-warning-text">
                      <Award className="h-4 w-4" /> {t('loyaltyPointsLabel', { points: loyaltyInfo.loyaltyPoints.toLocaleString() })}
                    </span>
                    <span className="text-xs text-warning-text">
                      {t('worthAmount', { amount: formatCurrency(loyaltyInfo.cashValue) })}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min={0}
                      max={loyaltyInfo.loyaltyPoints}
                      step={1}
                      placeholder={t('pointsToRedeem')}
                      value={redeemPointsInput}
                      onChange={(e) => setRedeemPointsInput(e.target.value)}
                      className="flex-1 h-11 text-sm"
                    />
                    {redeemPoints > 0 && !loyaltyPreviewFailed && (
                      <span className="shrink-0 text-xs font-medium text-end">
                        <span className="text-success-text">
                          {t('amountOff', { amount: formatCurrency(appliedLoyaltyDiscount) })}
                        </span>
                        {loyaltyPointsConsumed > 0 && loyaltyPointsConsumed < redeemPoints && (
                          <span className="block text-warning-text">
                            {t('pointsPartiallyApplied', { applied: loyaltyPointsConsumed, requested: redeemPoints })}
                          </span>
                        )}
                      </span>
                    )}
                    {redeemBelowMinimum && (
                      <span className="shrink-0 text-xs font-medium text-warning-text">
                        {t('redeemBelowMinimum', { min: minRedeemPoints })}
                      </span>
                    )}
                    {redeemPoints > 0 && loyaltyPreviewFailed && (
                      <span className="shrink-0 text-xs font-medium text-warning-text">
                        {t('loyaltyPreviewFailedShort')}
                      </span>
                    )}
                    {redeemPointsInput && (
                      <button
                        type="button"
                        onClick={() => setRedeemPointsInput('')}
                        aria-label={t('clearPoints')}
                        className="inline-flex h-11 w-11 shrink-0 items-center justify-center text-muted-foreground hover:text-foreground"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Coupon Code — only when promotions addon is active */}
            {canUsePromotions && (
            <div className="pb-2">
              {appliedCoupon ? (
                <div className="flex items-center justify-between rounded-lg bg-success/10 border border-success/15 px-3 py-2 text-sm">
                  <span className="flex items-center gap-1 text-success-text font-medium"><Tag className="h-4 w-4" /> {appliedCoupon.name}</span>
                  <button
                    type="button"
                    className="inline-flex h-11 w-11 items-center justify-center text-success-text hover:text-destructive-text ms-2 font-bold"
                    onClick={() => setAppliedCoupon(null)}
                    aria-label={t('removeCoupon')}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Input
                    placeholder={t('couponPlaceholder')}
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleApplyCoupon()}
                    className="flex-1 h-11 text-sm"
                  />
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={handleApplyCoupon}
                    disabled={couponLoading || !couponCode.trim()}
                    className="min-h-[44px] text-sm"
                  >
                    {couponLoading ? '...' : t('apply')}
                  </Button>
                </div>
              )}
            </div>
            )}

            {/* Amount input (single payment only) */}
            {!splitMode && (
            <div className="space-y-3 mb-6">
              <div>
                <Label>{t('amountTendered')}</Label>
                <Input
                  ref={amountRef}
                  type="number"
                  value={amountTendered}
                  onChange={(e) => setAmountTendered(e.target.value)}
                  className="text-lg font-semibold h-12"
                  min={0}
                  step={1}
                />
              </div>

              {/* Quick amount buttons for cash */}
              {selectedMethod === 'CASH' && (
                <div className="flex gap-2">
                  {quickAmounts.map((amt) => (
                    <Button
                      key={amt}
                      variant="outline"
                      size="lg"
                      onClick={() => setAmountTendered(String(amt))}
                      className="flex-1 text-base"
                    >
                      {formatCurrency(amt)}
                    </Button>
                  ))}
                </div>
              )}

              {/* Reference for non-cash */}
              {selectedMethod !== 'CASH' && selectedMethod !== 'CHEQUE' && (
                <div>
                  <Label>{t('referenceId')}{!paymentNeedsReference(selectedMethod) ? ` (${t('optionalLabel')})` : ''}</Label>
                  <Input
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    placeholder={t('transactionRef')}
                  />
                </div>
              )}

              {/* Receiving bank account — every bank-settled method (hidden when <= 1 account) */}
              <BankAccountField
                method={selectedMethod}
                accounts={bankAccounts}
                value={selectedBankAccountId}
                onChange={setSelectedBankAccountId}
              />

              {/* Cheque particulars */}
              {selectedMethod === 'CHEQUE' && <ChequeFields value={chequeForm} onChange={setChequeForm} />}

              {/* Change display */}
              {change > 0 && selectedMethod === 'CASH' && (
                <div className="rounded-lg bg-gradient-to-r from-success/10 to-success/10 border border-success/15 p-3 text-center">
                  <p className="text-sm text-muted-foreground">{t('change')}</p>
                  <p className="text-2xl font-bold text-success-text">
                    {formatCurrency(change)}
                  </p>
                </div>
              )}
            </div>
            )}

            {/* Action buttons */}
            </div>
            <div className="shrink-0 px-6 pb-6 pt-3 border-t bg-card flex gap-3">
              <Button variant="outline" onClick={onClose} className="flex-1 h-12 text-base">
                {t('cancel', { ns: 'common' })}
              </Button>
              <span className="flex-1 flex" title={isViewOnly ? t('viewOnlyCheckoutTooltip') : undefined}>
              <Button
                onClick={() => handleSubmit()}
                disabled={
                  isViewOnly ||
                  saleMutation.isPending ||
                  (!splitMode && selectedMethod === 'CREDIT' && customer !== null && creditInfo !== null &&
                    creditInfo.availableCredit !== null && effectiveGrandTotal > creditInfo.availableCredit) ||
                  (splitMode
                    ? splitEval.status === 'short'
                    : Number(amountTendered || 0) < effectiveGrandTotal)
                }
                className="w-full h-12 text-lg bg-gradient-to-r from-primary via-primary to-primary hover:from-primary/90 hover:via-primary/90 hover:to-primary/90 text-primary-foreground shadow-lg shadow-primary/25"
              >
                {saleMutation.isPending ? (
                  <Loader2 className="me-2 h-5 w-5 animate-spin" />
                ) : null}
                {t('completeSale')}
              </Button>
              </span>
            </div>
          </div>
        )}
      </div>
    </Dialog>

      {/* Serial Number Selector Dialog */}
      <Dialog
        visible={showSerialSelector}
        onHide={() => setShowSerialSelector(false)}
        modal
        header={t('selectSerialNumbers')}
        style={{ width: '42rem', maxWidth: '95vw' }}
        footer={
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={() => setShowSerialSelector(false)}>
              {t('back', { ns: 'common' })}
            </Button>
            <Button
              className="flex-1 bg-gradient-to-r from-primary to-primary text-primary-foreground"
              onClick={() => {
                setShowSerialSelector(false);
                handleSubmit();
              }}
            >
              {t('continueToPayment')}
            </Button>
          </div>
        }
      >
        <div className="space-y-4 py-2">
          <p className="text-sm text-muted-foreground mb-4">
            {t('selectSerialHint')}
          </p>
          {items.map((item, _idx) => {
            if (!item.product.serialTracking) return null;

            // Unit number within the same product (for multi-unit display)
            const sameProductItems = items.filter((i) => i.product.serialTracking && i.product.id === item.product.id);
            const unitNo = sameProductItems.findIndex((i) => i.id === item.id) + 1;

            // Serials already selected for OTHER cart lines of the same product (will be disabled, not hidden)
            const otherSelectedSerials = new Set(
              items
                .filter((i) => i.id !== item.id && i.product.id === item.product.id && i.serialNumberId)
                .map((i) => i.serialNumberId!)
            );
            const productSerials = availableSerials.filter((s) => s.productId === item.product.id);
            const selectedId = item.serialNumberId;

            return (
              <div key={item.id} className="rounded-lg border border-info/30 bg-info/10 p-4">
                <p className="text-sm font-semibold mb-1">
                  {item.product.name}
                  {sameProductItems.length > 1 && (
                    <span className="ms-2 text-xs font-normal text-info-text">{t('unitOf', { unit: unitNo, total: sameProductItems.length })}</span>
                  )}
                </p>
                <p className="text-xs text-muted-foreground mb-2">{t('selectSerialHint')}</p>
                <Dropdown
                  value={selectedId || ''}
                  onChange={(e) => {
                    const v = e.value as string;
                    useCartStore.getState().setItemSerialNumber(item.id, v || null);
                    setSelectedSerials({ ...selectedSerials, [item.id]: v });
                  }}
                  options={[
                    { label: `— ${t('selectSerialNumbers')} —`, value: '', disabled: false },
                    ...productSerials.map((serial) => {
                      const isTaken = otherSelectedSerials.has(serial.id);
                      return {
                        label: `${serial.serialNo}${isTaken ? ` ${t('serialTakenByUnit')}` : ''}`,
                        value: serial.id,
                        disabled: isTaken,
                      };
                    }),
                    ...(productSerials.length === 0
                      ? [{ label: t('noAvailableSerials'), value: '__none__', disabled: true }]
                      : []),
                  ]}
                  optionLabel="label"
                  optionValue="value"
                  optionDisabled="disabled"
                  className="w-full"
                />
                {productSerials.length === 0 && (
                  <p className="mt-2 text-xs text-destructive-text">
                    <AlertTriangle className="inline h-3.5 w-3.5 me-1 align-text-bottom" />{t('noAvailableSerialsHint')}
                  </p>
                )}
                {selectedId && (
                  <p className="mt-1 text-xs text-success-text">
                    <Check className="inline h-3.5 w-3.5 me-1 align-text-bottom" />{t('serialSelected')}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </Dialog>

      {/* Manager Approval Dialog (T-001) */}
      <ManagerApprovalDialog
        open={managerApprovalOpen}
        reason={t('managerApprovalReason', {
          pct: orderDiscountPct.toFixed(0),
          threshold: managerDiscountThreshold,
        })}
        onApproved={(manager) => {
          setManagerApprovalOpen(false);
          toast.success(t('toastApprovedBy', { name: manager.name }));
          handleSubmit(true);
        }}
        onCancel={() => setManagerApprovalOpen(false)}
      />
    </>
  );
}
